import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import {
  copyTextToClipboard,
  getPinWithEdition,
} from 'src/app/utility/app.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as _sanitizeHTML from 'sanitize-html';
import { ExamsService } from 'src/app/api/services';
import { formatDate } from '@angular/common';
import { DialogService } from './dialog.service';
import dayjs from 'dayjs';
import { asShortLink } from 'src/app/app.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FormSelectComponent } from './form-field/select.component';
import { UntypedFormControl } from '@angular/forms';

const sanitizeHTML = (_sanitizeHTML as any).default || _sanitizeHTML;

export interface InvitationTextDialogData {
  name: string;
  mode: string;
  pin_code: any;
  unsupervisedExam?: {
    startTime: any;
    endTime: any;
    timeToSolve: number;
  };
  interactive?: boolean;
  examId?: number;
  username?: string;
  onMailSent: (missingEmails: string[]) => void;
  address?: string;
  title?: string;
  text?: string;
  subject?: string;
  usersYetToStart?: any;
  sendMailCallback?: (subject: string, content: string) => void;
}

@Component({
  selector: 'qa-invitationtext-dialog',
  templateUrl: './invitationtext-dialog.component.html',
  styleUrls: ['./invitationtext-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatIcon,
    TranslateModule,
    FormSelectComponent,
  ],
})
export class InvitationTextDialogComponent implements OnInit {
  text: string;
  blurInput = true;
  @ViewChild('textArea') textArea: ElementRef;
  sendTo = new UntypedFormControl();

  sendMailOptions = [
    {
      name: this.translator.instant('invitationTextDialog.sendToEveryone'),
      value: 'everyone',
    },
    {
      name: this.translator.instant(
        'invitationTextDialog.sendToUsersYetToStart'
      ),
      value: 'usersYetToStart',
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InvitationTextDialogData,
    private matSnackBar: MatSnackBar,
    private translator: TranslateService,
    private dialogRef: MatDialogRef<InvitationTextDialogComponent>,
    private examsService: ExamsService,
    private dialog: DialogService
  ) {}

  ngOnInit() {
    this.dialogRef.updateSize('800px');
    if (!this.data.text) {
      this.initText();
    } else {
      this.text = this.data.text;
    }
    if (this.data.interactive) {
      this.sendTo.setValue(this.sendMailOptions[0]);
    }
  }

  private initText(): void {
    this.text = this.translator.instant('invitationTextDialog.text1', {
      unit:
        this.data.mode === 'live-event'
          ? this.translator.instant('liveEvent.name')
          : this.translator.instant('exam.name'),
      unitName: this.data.name,
      username:
        this.data.mode === 'exam' &&
        (this.data.interactive || this.data.username)
          ? ' %_USER_NAME_%'
          : '',
    });

    //FIXME: interactive check needed?
    if (
      this.data.mode === 'exam' &&
      (this.data.interactive || this.data.username)
    ) {
      this.text += this.translator.instant('exam.invitation.username');
    }

    this.text += this.translator.instant('invitationTextDialog.text2', {
      url: asShortLink(this.data.pin_code),
      pinCode: getPinWithEdition(this.data.pin_code),
    });

    if (this.data.mode === 'exam' && this.data.unsupervisedExam) {
      this.text += `${this.translator.instant(`exam.invitation.unsupInfo`, {
        startTime: dayjs(this.data.unsupervisedExam.startTime).format(
          'DD.MM.YYYY, HH:mm'
        ),
        lastStartTime: dayjs(this.data.unsupervisedExam.endTime).format(
          'DD.MM.YYYY, HH:mm'
        ),
        duration: this.data.unsupervisedExam.timeToSolve.toString(),
      })}\n`;
    }

    this.text += this.translator.instant(
      'invitationTextDialog.appsDisclaimer',
      {
        unitReference:
          this.data.mode === 'exam'
            ? this.translator.instant('exam.invitation.unitReference')
            : this.translator.instant('liveEvent.invitation.unitReference'),
      }
    );

    this.text += this.translator.instant(
      `${
        this.data.mode === 'live-event' ? 'liveEvent' : 'exam'
      }.invitation.tipps`
    );
  }

  parseText(text) {
    text = text.replace(
      /(https?:\/\/(?:[^,\)\n\s]*))([\n|,|\)|\s])/gi,
      (_, group, suffix) => {
        return `<a href="${group}">${group}</a>${suffix}`;
      }
    );
    text = text.replace(/%\_(.*)\_%/gim, (match) => {
      return `<span class='placeholder'>${match}</span>`;
    });
    return this.sanitize(text);
  }

  private sanitize(input: string): string {
    return sanitizeHTML(input, {
      allowedTags: ['a', 'span'],
      allowedAttributes: {
        '*': ['href', 'title', 'class'],
      },
    });
  }

  onBlur() {
    this.text = this.textArea.nativeElement.value;
    this.blurInput = !!this.text;
  }

  onEdit() {
    this.blurInput = false;
    setTimeout(() => {
      if (this.textArea) {
        this.textArea.nativeElement.focus();
      }
    }, 100);
  }

  replaceLocally(): string {
    return this.text.replace(/%\_(.*)\_%/gm, (_, group) => {
      switch (group) {
        case 'PIN_CODE':
          return `${this.data.pin_code}`;
        case `USER_NAME`:
          return this.data.username ?? '[USER NAME]';
        case `START_TIME`:
          return `${
            this.data.unsupervisedExam.startTime
              ? formatDate(
                  this.data.unsupervisedExam.startTime,
                  'd.M.yyyy HH:mm:ss',
                  'en-US'
                )
              : 'N/A'
          }`;
        case 'LAST_START_TIME':
          return `${
            this.data.unsupervisedExam.endTime
              ? formatDate(
                  this.data.unsupervisedExam.endTime,
                  'd.M.yyyy HH:mm:ss',
                  'en-US'
                )
              : 'N/A'
          }`;
        case 'DURATION':
          return `${
            this.data.unsupervisedExam.timeToSolve
              ? this.data.unsupervisedExam.timeToSolve
              : 0
          }`;
        default:
          return `[${group}]`;
      }
    });
  }

  copyWeblink() {
    if (this.text) {
      try {
        copyTextToClipboard(this.replaceLocally());
        this.matSnackBar.open(
          this.translator.instant('invitationTextDialog.copiedToClipboard')
        );
      } catch (error) {
        this.matSnackBar.open(
          this.translator.instant('invitationTextDialog.notCopiedToClipboard')
        );
      }
    } else {
      this.matSnackBar.open(
        this.translator.instant('invitationTextDialog.notCopiedToClipboard')
      );
    }
  }

  parseToMailLink() {
    return `mailto:${this.data?.address || ''}?subject=${encodeURIComponent(
      this.data?.subject ??
        this.translator.instant('common.label.invitationTo', {
          name: this.translator.instant(
            this.data.mode === 'live-event'
              ? 'liveEvent.setup.aLiveEvent'
              : 'exam.setup.anExam'
          ),
        })
    )}&body=${encodeURIComponent(this.replaceLocally())}`;
  }

  sendMail() {
    const subject =
      this.data?.subject ??
      this.translator.instant('common.label.invitationTo', {
        name: this.translator.instant(
          this.data.mode === 'live-event'
            ? 'liveEvent.setup.aLiveEvent'
            : 'exam.setup.anExam'
        ),
      });
    const content = this.text;
    if (this.data.sendMailCallback) {
      this.data.sendMailCallback(subject, content);
      return;
    }
    if (this.data.interactive) {
      const emailList = this.data.usersYetToStart.map((x) => x.email);
      this.examsService
        .sendEmailInvitationToExamUsers({
          id: this.data.examId,
          body: {
            email: {
              content,
              subject,
            },
            mail_list:
              this.sendTo.value.value === 'usersYetToStart' ? emailList : [],
          },
        })
        .subscribe((val: any) => {
          const resp = JSON.parse(val);
          if (resp.missing_emails && resp.missing_emails.length) {
            this.dialog.error(
              'Error',
              this.translator.instant('exam.setup.noMails', {
                missing_emails: resp.missing_emails
                  .map((mail) => ` -  ${mail}`)
                  .join('\n'),
              })
            );
          } else {
            this.matSnackBar.open(
              this.translator.instant('exam.setup.sentInvitation')
            );
          }
          if (this.data.onMailSent) {
            this.data.onMailSent(resp?.missing_emails || []);
          }
        });
    } else {
      window.open(this.parseToMailLink());
      if (this.data.onMailSent && this.data?.username) {
        this.data.onMailSent([]);
      }
    }
  }

  compareFn = (x, y): boolean => (x && y ? x.value === y.value : false);
}
